import React, { useState } from "react"
import { CHART_PALETTE } from "./chartTheme"

const DEFAULT_COLORS = [CHART_PALETTE[2], { color1: "#C5CAE9" }]

const barStyle = (color1, color2) =>
  color2
    ? { backgroundImage: `linear-gradient(to bottom, ${color1}, ${color2})` }
    : { backgroundColor: color1 }

const CHART_H = 240
const CONTAINER_H = 360
const PAD_TOP = 40
const PAD_BOTTOM = 60

const formatValue = (val) => {
  if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(0)}M`
  if (val >= 1_000) return `${(val / 1_000).toFixed(0)}K`
  return Math.round(val)
}

// series: [
//   { name: string, color1?: string, color2?: string, data: number[], meta?: string[] },
//   { name: string, color1?: string, color2?: string, data: number[], meta?: string[] },
// ]
// series[0] = foreground bar (e.g. completed)
// series[1] = background bar additive (e.g. uncompleted/remaining)
// meta: optional per-data-point extra string shown in the tooltip below the value
// labels: string[]  — x-axis labels, one per data point
function DualBarChart({ labels = [], series = [] }) {
  const [tooltip, setTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    index: null,
  })
  const [hoveredIndex, setHoveredIndex] = useState(null)

  const fgSeries = series[0] || { name: "", ...DEFAULT_COLORS[0], data: [] }
  const bgSeries = series[1] || { name: "", ...DEFAULT_COLORS[1], data: [] }

  const fgColor1 = fgSeries.color1 || DEFAULT_COLORS[0].color1
  const fgColor2 = fgSeries.color2 || null
  const bgColor1 = bgSeries.color1 || DEFAULT_COLORS[1].color1
  const bgColor2 = bgSeries.color2 || null

  const totals = labels.map(
    (_, i) => (fgSeries.data[i] || 0) + (bgSeries.data[i] || 0),
  )
  const maxTotal = Math.max(...totals, 1)

  return (
    <div
      style={{
        position: "relative",
        height: CONTAINER_H,
        display: "flex",
        alignItems: "flex-end",
        padding: `0 20px 0 70px`,
        boxSizing: "border-box",
        zIndex: 0,
      }}
    >
      {/* Grid lines */}
      <div
        style={{
          position: "absolute",
          top: PAD_TOP,
          left: 0,
          right: 0,
          bottom: PAD_BOTTOM,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          pointerEvents: "none",
          zIndex: 1,
        }}
      >
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            style={{
              position: "relative",
              width: "100%",
              borderTop: "2px dashed #e2e8f0",
              height: 0,
            }}
          >
            {i < 4 && (
              <span
                style={{
                  position: "absolute",
                  left: 8,
                  bottom: 4,
                  fontSize: 12,
                  color: "#64748b",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                }}
              >
                {formatValue((maxTotal * (4 - i)) / 4)}
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Baseline */}
      <div
        style={{
          position: "absolute",
          bottom: PAD_BOTTOM,
          left: 0,
          right: 0,
          height: 2,
          backgroundColor: "#e2e8f0",
          zIndex: 2,
        }}
      />

      {/* Tooltip */}
      {tooltip.visible && tooltip.index !== null && (
        <div
          style={{
            position: "absolute",
            left: tooltip.x,
            top: tooltip.y,
            transform: "translate(-50%, -100%)",
            backgroundColor: "#1e293b",
            color: "#fff",
            borderRadius: 8,
            padding: "8px 12px",
            fontSize: 12,
            pointerEvents: "none",
            zIndex: 10,
            whiteSpace: "nowrap",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            marginTop: -8,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4, color: "#e2e8f0" }}>
            {labels[tooltip.index]}
          </div>
          {[fgSeries, bgSeries].map((s, si) => {
            const c1 = si === 0 ? fgColor1 : bgColor1
            const c2 = si === 0 ? fgColor2 : bgColor2
            const meta = s.meta?.[tooltip.index]
            return (
              <div key={si} style={{ marginBottom: si === 0 ? 4 : 0 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    color: "#fff",
                  }}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      flexShrink: 0,
                      ...barStyle(c1, c2),
                    }}
                  />
                  {s.name}:{" "}
                  <strong style={{ color: "#fff" }}>
                    {s.data[tooltip.index] ?? 0}
                  </strong>
                </div>
                {meta && (
                  <div
                    style={{
                      color: "#94a3b8",
                      fontSize: 11,
                      marginTop: 2,
                      paddingLeft: 14,
                    }}
                  >
                    {meta}
                  </div>
                )}
              </div>
            )
          })}
          <div
            style={{
              position: "absolute",
              bottom: -5,
              left: "50%",
              transform: "translateX(-50%)",
              width: 10,
              height: 10,
              backgroundColor: "#1e293b",
              clipPath: "polygon(0 0, 100% 0, 50% 100%)",
            }}
          />
        </div>
      )}

      {/* Bars */}
      {labels.map((label, i) => {
        const bgHeight = (totals[i] / maxTotal) * CHART_H
        const fgHeight = ((fgSeries.data[i] || 0) / maxTotal) * CHART_H

        return (
          <div
            key={label}
            onMouseMove={(e) => {
              const rect = e.currentTarget.parentElement.getBoundingClientRect()
              setTooltip({
                visible: true,
                x: e.clientX - rect.left,
                y: e.clientY - rect.top - 8,
                index: i,
              })
              setHoveredIndex(i)
            }}
            onMouseLeave={() => {
              setTooltip((prev) => ({ ...prev, visible: false }))
              setHoveredIndex(null)
            }}
            style={{
              position: "relative",
              flex: 1,
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 3,
              cursor: "default",
            }}
          >
            {/* Hover highlight */}
            {hoveredIndex === i && (
              <div
                style={{
                  position: "absolute",
                  bottom: PAD_BOTTOM,
                   paddingBottom: 0,
                  width: 69,
                  height: bgHeight + 8,
                  borderTopLeftRadius: 18,
                  borderTopRightRadius: 18,
                  backgroundColor: "rgba(148, 163, 184, 0.18)",
                  pointerEvents: "none",
                  transition: "opacity 0.15s ease",
                }}
              />
            )}

            {/* Background bar (total) */}
            <div
              style={{
                position: "absolute",
                bottom: PAD_BOTTOM,
                width: 55,
                height: bgHeight,
                borderRadius: 14,
                transition: "height 0.5s ease",
                ...barStyle(bgColor1, bgColor2),
              }}
            />

            {/* Foreground bar */}
            <div
              style={{
                position: "absolute",
                bottom: PAD_BOTTOM,
                width: 55,
                height: fgHeight,
                borderBottomLeftRadius: 14,
                borderBottomRightRadius: 14,
                transition: "height 0.5s ease",
                ...barStyle(fgColor1, fgColor2),
              }}
            >
              {fgHeight > 0 && (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: -4,
                    right: -4,
                    height: 6,
                    backgroundColor: fgColor1,
                    borderRadius: 3,
                  }}
                />
              )}
            </div>

            {/* X-axis label */}
            <div
              style={{
                position: "absolute",
                bottom: 16,
                fontSize: 13,
                color: "#64748b",
                textAlign: "center",
                fontWeight: 500,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "100%",
              }}
            >
              {label}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default DualBarChart
